import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import ScissorsMascot from '../components/ScissorsMascot';
import { toast } from 'sonner';
import { Loader2, Globe, Mail, Users as UsersIcon } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [lookAt, setLookAt] = useState({ x: 0, y: 0 });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code+password
  const [resetLoading, setResetLoading] = useState(false);
  const [stats, setStats] = useState({ total_users: 0, online_users: 0 });
  const statsIntervalRef = React.useRef(null);
  
  const { login, requestPasswordReset, resetPassword } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiClient.get('/stats/platform');
        setStats({
          total_users: response.data.total_users,
          online_users: response.data.online_users
        });
      } catch (error) {
        console.error('Failed to fetch platform stats:', error);
      }
    };
    fetchStats();
    // Poll stats every 5 minutes (less frequent to reduce server load)
    statsIntervalRef.current = setInterval(fetchStats, 300000);
    return () => {
      if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
    };
  }, []);

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    // Simple logic to look at the text length
    const offsetX = Math.min(Math.max((val.length / 30) * 2 - 1, -1), 1);
    setLookAt({ x: offsetX, y: 1 }); // Always looking down at the field
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Stop stats polling immediately to free up server
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }

    try {
      // Clear token to trigger SSE close in useSSE hook
      localStorage.removeItem('token');
      
      // Small delay to allow potential background requests to finish
      // on the single-threaded server before we hit the login endpoint
      await new Promise(resolve => setTimeout(resolve, 500));
      await login(email, password);
      toast.success(t('welcomeMessage'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('loginFailed'));
      // Restart polling if login fails
      const fetchStats = async () => {
        try {
          const response = await apiClient.get('/stats/platform');
          setStats({
            total_users: response.data.total_users,
            online_users: response.data.online_users
          });
        } catch (error) {}
      };
      statsIntervalRef.current = setInterval(fetchStats, 300000);
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const result = await requestPasswordReset(resetEmail);
      toast.success(language === 'de' 
        ? `Reset-Code: ${result.verification_code}` 
        : `Reset code: ${result.verification_code}`
      );
      setResetStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await resetPassword(resetEmail, resetCode, newPassword);
      toast.success(language === 'de' ? 'Passwort zurückgesetzt!' : 'Password reset!');
      setShowForgotPassword(false);
      setResetStep(1);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="auth-container" data-testid="login-page">
      <LanguageSelector
        className="absolute top-[calc(1rem+env(safe-area-inset-top))] right-4 z-10 border border-border bg-background/50 backdrop-blur-md rounded-xl"
        data-testid="login-language-toggle"
      />

      {!showForgotPassword ? (
        <Card className="auth-card shadow-lg">
          <CardHeader className="text-center pb-2 sm:pb-6 px-0 sm:px-6">
            <div className="flex justify-center mb-2 sm:mb-4">
              <ScissorsMascot 
                size="md" 
                lookAt={lookAt.x} 
                lookAtY={lookAt.y}
                isPassword={isPasswordFocused} 
              />
            </div>
            <CardTitle className="text-2xl sm:text-3xl font-extrabold tracking-tight">{t('welcomeBack')}</CardTitle>
            <CardDescription className="text-sm sm:text-base">{t('signInToAccount')}</CardDescription>
            
            {/* Stats Badge */}
            <div className="flex items-center justify-center gap-3 mt-4 text-[9px] sm:text-[10px] uppercase font-bold tracking-widest text-muted-foreground bg-secondary/40 py-2 rounded-full px-4 w-fit mx-auto border border-border/50">
              <div className="flex items-center gap-1.5">
                <UsersIcon className="w-3 h-3 text-red-500" />
                <span>{stats.total_users} User</span>
              </div>
              <div className="w-px h-3 bg-border/50" />
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <span>{stats.online_users} Online</span>
              </div>
            </div>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 pt-4 px-0 sm:px-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs sm:text-sm font-semibold ml-1">{t('email')}</Label>
                <Input
                  id="email"
                  type="text"
                  placeholder={language === 'de' ? "E-Mail oder Benutzername" : "Email or Username"}
                  value={email}
                  onChange={handleEmailChange}
                  onFocus={() => setIsPasswordFocused(false)}
                  required
                  className="h-12 text-base rounded-xl"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs sm:text-sm font-semibold ml-1">{t('password')}</Label>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-[11px] sm:text-xs text-muted-foreground hover:text-red-500 transition-colors"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    {language === 'de' ? 'Passwort vergessen?' : 'Forgot password?'}
                  </Button>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setIsPasswordFocused(true)}
                  onBlur={() => setIsPasswordFocused(false)}
                  required
                  className="h-12 text-base rounded-xl"
                  data-testid="login-password-input"
                />
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-6 pt-2">
              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700 h-12 text-base font-bold rounded-xl shadow-lg shadow-red-600/20 active:scale-[0.98] transition-all"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('signingIn')}
                  </>
                ) : (
                  t('signIn')
                )}
              </Button>

              <div className="flex flex-col items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  {t('noAccount')}{' '}
                  <Link to="/register" className="text-red-500 hover:text-red-400 font-bold transition-colors" data-testid="register-link">
                    {t('createOne')}
                  </Link>
                </p>
                <div className="flex gap-6 text-[11px] font-medium text-muted-foreground/50 uppercase tracking-wider">
                  <Link to="/impressum" className="hover:text-foreground transition-colors">Impressum</Link>
                  <Link to="/datenschutz" className="hover:text-foreground transition-colors">{language === 'de' ? 'Datenschutz' : 'Privacy'}</Link>
                </div>
              </div>
            </CardFooter>
          </form>
        </Card>
      ) : (
        <Card className="auth-card shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold">
              {language === 'de' ? 'Passwort zurücksetzen' : 'Reset Password'}
            </CardTitle>
            <CardDescription className="text-sm">
              {resetStep === 1 
                ? (language === 'de' ? 'Gib deine E-Mail ein' : 'Enter your email')
                : (language === 'de' ? 'Gib den Code und neues Passwort ein' : 'Enter code and new password')
              }
            </CardDescription>
          </CardHeader>

          {resetStep === 1 ? (
            <form onSubmit={handleRequestReset}>
              <CardContent className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold ml-1">{t('email')}</Label>
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="h-12 text-base rounded-xl"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4 pt-2">
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 h-12 rounded-xl font-bold" disabled={resetLoading}>
                  {resetLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                  {language === 'de' ? 'Code senden' : 'Send code'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowForgotPassword(false)} className="text-muted-foreground">
                  {t('back')}
                </Button>
              </CardFooter>
            </form>
          ) : (
            <form onSubmit={handleResetPassword}>
              <CardContent className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold ml-1">{language === 'de' ? 'Reset-Code' : 'Reset Code'}</Label>
                  <Input
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    placeholder="123456"
                    required
                    className="h-12 text-base font-mono rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold ml-1">{language === 'de' ? 'Neues Passwort' : 'New Password'}</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    required
                    className="h-12 text-base rounded-xl"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4 pt-2">
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 h-12 rounded-xl font-bold" disabled={resetLoading}>
                  {resetLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {language === 'de' ? 'Passwort ändern' : 'Change Password'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => { setResetStep(1); setShowForgotPassword(false); }} className="text-muted-foreground">
                  {t('back')}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      )}
    </div>
  );
};

export default Login;
